/*
 * Copyright (C) 2025 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { CommonModule } from '@angular/common';
import { booleanAttribute, Component, Input, OnInit } from '@angular/core';
import { RouterLink } from '@angular/router';
import { cva, VariantProps } from 'class-variance-authority';
import { uniqueId } from 'lodash-es';
import { BadgeComponent, BadgeVariants } from '../../badge/badge.component';

export const preColVariants = cva('flex shrink-0 gap-2 text-lg empty:hidden', {
  variants: {
    size: {
      default: '',
      '1/3': '@xl:w-1/3',
      '1/4': '@xl:w-1/4',
    },
  },
  defaultVariants: { size: 'default' },
});
export type PreColVariants = VariantProps<typeof preColVariants>;

export interface CardColBadge {
  name: string;
  variant?: BadgeVariants['variant'];
  color?: BadgeVariants['color'];
  dataTestId?: string;
}

@Component({
  selector: 'ods-card-col',
  imports: [CommonModule, RouterLink, BadgeComponent],
  template: `
    <div [ngClass]="preColVariants({ size: preSize })" [attr.id]="preColId">
      <ng-content select="[pre]" />
      @if (pre) {
        <p>{{ pre }}</p>
      }
    </div>

    <div class="flex grow flex-col">
      <div class="flex flex-wrap items-center gap-2 text-lg font-medium">
        @if (linkTo) {
          <a
            [routerLink]="linkTo"
            class="outline-hidden after:absolute after:inset-0 after:block after:content-[''] hover:underline focus-visible:underline"
            [attr.aria-describedby]="isLinkDescribedByPre ? preColId : null"
          >
            <ng-container [ngTemplateOutlet]="titleContent" />
          </a>
        } @else {
          <ng-container [ngTemplateOutlet]="titleContent" />
        }
        <div class="flex flex-wrap gap-2">
          @for (badge of badges; track $index) {
            <ods-badge
              [name]="badge.name"
              [variant]="badge.variant"
              [color]="badge.color"
              [attr.data-test-id]="badge.dataTestId"
            />
          }
        </div>
      </div>
      <p>{{ subtitle }}</p>
      <p class="text-lg">{{ text }}</p>
      <ng-content />
    </div>

    <ng-template #titleContent>
      @if (isListItem) {
        <strong>{{ title }}</strong>
      } @else {
        <div role="heading" [attr.aria-level]="headingLevel" [id]="headingId">
          {{ title }}
        </div>
      }
    </ng-template>
  `,
  host: {
    class: 'flex-auto flex flex-col gap-2 text-text wrap-anywhere empty:hidden @xl:flex-row @xl:w-1/2',
    '[attr.title]': 'null',
  },
})
export class CardColComponent implements OnInit {
  @Input() title: string;
  @Input() subtitle: string;
  @Input() text: string;
  @Input() linkTo: string;
  @Input({ transform: booleanAttribute }) isLinkDescribedByPre: boolean;
  @Input() pre: string;
  @Input() preSize: PreColVariants['size'];
  @Input() badges: CardColBadge[];
  @Input() headingLevel: 2 | 3 | 4 | 5 | 6 = 4;

  public isListItem: boolean;
  public preColId: string;
  public headingId: string;
  public readonly preColVariants = preColVariants;

  ngOnInit(): void {
    this.preColId = uniqueId('pre-col-');
  }
}
